import React, {Fragment} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {Link} from 'react-router-dom'
import {ICar} from '../../../../models/Car'
import {IHolder} from '../../../../models/Holder'
import {ICarLocation} from '../../../../models/CarLocation'
import { IRouteComponentProps } from '../../containers/DataResolver'
import {carReviewAllPageSize, urlPrefixes, StationType, prefectureToArea, jaPrefectureType} from '../../../config/constants'
import Pager from '../../common/Pager'
import LocaleText from '../../common/LocaleText'
import ResponsiveImg from '../../common/ResponsiveImg'
import Breadcrumb from '../../containers/Breadcrumb'
import {useLocale, useUserType, useQuery, useSEO} from '../../hocs/hooks'
import carStyles from './Car.scss'
import styles from './CarReview.scss'
import CarPanel from '../cars/CarPanel'
import CarReviewPanel, {CarStar, IReview} from './CarReviewPanel'
import { titlesMessages, descriptionMessages } from '../../../config/commonMessages'
import {getFullName} from '../../../helpers/util'
import {getAreaLink} from '../../carOrStation/StationCarList'
import {localizeCarLocation} from '../../../helpers/carLocation'
import {localizeCar} from '../../../helpers/car'
import link from '../../../assets/imgs/cars/link.png'
import superHolder from '../../../assets/imgs/cars/super-holder.png'
import defaultProfile from '../../../assets/imgs/profile-default.png'

const CarReviewAll = (
  {
    resolvedData
  }:IRouteComponentProps<{
    car: ICar
    reviews: ReadonlyArray<IReview>
    locations: ReadonlyArray<ICarLocation>
    holder: IHolder
  }>
) => {
  const locale = useLocale()
  const userType = useUserType()
  const {page} = useQuery()
  const intl = useIntl()
  const carLocation = localizeCarLocation(locale, resolvedData.locations[0])
  const cars = localizeCar(locale, resolvedData.car)
  const reviews = resolvedData.reviews
  const holder = resolvedData.holder
  const prefecture = jaPrefectureType[carLocation.prefecture]
  const area = prefectureToArea[carLocation.prefecture]
  const {
    totalReviews,
    stars,
    name,
  } = cars
  useSEO({
    title: intl.formatMessage(titlesMessages.carReviewAllWithName, {name}),
    description: intl.formatMessage(descriptionMessages.carReviewAll, {name}),
    canonicalUrl: !page
      ? `/${locale}${urlPrefixes.user}/cars/${resolvedData.car._id}/reviews/`
      : `/${locale}${urlPrefixes.user}/cars/${resolvedData.car._id}/reviews/?page=${page}`
  })
  return <>
  <Breadcrumb>
      <Link to={`/${locale}${urlPrefixes[userType]}/`}>
        <FormattedMessage id="navbar.home"/>
      </Link>
      {getAreaLink(locale, StationType.car, area)}
      <Link to={`/${locale}/car-list/${area}/${prefecture}/`}>
        <FormattedMessage id="breadcrumb.carsPrefecture" values={{prefecture: carLocation.prefecture}} />
      </Link>
      {name}
    </Breadcrumb>
   <div className={['d-md-flex row offset-md-1 mt-32px pt-md-2 pl-md-1', styles.addMargin, styles.container].join(' ')}>
  <div className={['col-md-6',styles.leftContainer].join(' ')}>
    <div className='mb-4 pb-2 pb-md-4'>
      <h1 className={styles.title}>
        <FormattedMessage
            id="reviewAll.totalReviews"
            values={{name, totalReviews}}
            defaultMessage="{totalReviews} reviews of {name}"
          />
      </h1>
    </div>
    <div className="form-group">
      <div className={['mb-4', carStyles.sectionTitle, styles.averageStar].join(' ')}>
        <CarStar stars={stars}/>
        <span className="pl-2 small">
        <FormattedMessage
          id="review.totalReviews"
          values={{totalReviews}}
          defaultMessage="({totalReviews} reviews)"
        />
        </span>
      </div>
      {reviews.map((review, i) => <Fragment key={i}>
        {i !== 0 && <hr className="d-block"/>}
        <CarReviewPanel review={review}/>
      </Fragment>
      )}
      <div className="d-flex justify-content-center">
        <Pager total={totalReviews} pageSize={carReviewAllPageSize} isVan/>
      </div>
    </div>
  </div>
  <div className={['offset-md-1 mb-4 col-md-4', styles.deleteSpPadding].join(' ')}>
  <div className={[styles.cardContainer].join(' ')}>
    <CarPanel car={resolvedData.car} locations={resolvedData.locations} viewReview/>
    {holder && <>
                <div className="mt-3 p-4 d-flex flex-column border rounded w-100 ">
                  <div className='d-flex flex-column'>
                    <div className="d-flex w-100">
                      <div
                        className={[
                          'col-md-2 col-2 p-md-0 pr-xs-0 d-flex justify-content-md-center align-items-center',
                          carStyles.topHolderIcon
                        ].join(' ')}
                      >
                        <ResponsiveImg
                          width={50}
                          height={50}
                          className="rounded-circle"
                          caption="Holder picture"
                          url={holder?.picture || defaultProfile}/>
                      </div>
                      <div className={['col-9 col-md-10 d-flex flex-column justify-content-center', carStyles.holder].join(' ')}>
                        <div className="font-weight-bold"><LocaleText ja="ホルダー" en="Holder" /></div>
                        <div>{getFullName(locale, holder)}{<LocaleText ja="さん" en="" />}</div>
                      </div>
                    </div>
                    <div className={[styles.holderIntro].join(' ')}>
                      {holder.introduction}
                    </div>
                  </div>
                  {holder.isSuperHolder && <div className={['mt-3', carStyles.superHolder].join(' ')}>
                    <a href="https://persistent-direction-0f1.notion.site/6a7813ebe6eb4672a38684459b175461">
                      <div className={[carStyles.superHolderArea, 'order-md-3 mt-2 d-flex align-items-center mb-0 text-body'].join(' ')}>
                        <div className="d-flex justify-content-between align-items-center border py-2 px-3 w-100">
                          <div className="d-flex align-items-center mr-2">
                            <ResponsiveImg
                              width={22}
                              height={24}
                              noLazy
                              className="mr-12px"
                              caption="Super holder"
                              url={superHolder}
                            />
                            <div className="font-size-10">
                              <div className="font-weight-bold"><LocaleText ja="スーパーホルダー" en="Super Holder" /></div>
                              <div>
                                <FormattedMessage
                                    id="reviewAll.superHolderDescription"
                                />
                              </div>
                            </div>
                          </div>
                          <div>
                            <ResponsiveImg
                              width={8}
                              height={14}
                              noLazy
                              className="ml-1"
                              caption="Link"
                              url={link}
                            />
                          </div>
                        </div>
                      </div>
                    </a>
                  </div>}
                </div>
                </>}
    <div className='offset-md-1' />
    </div>
  </div>
  </div>
  <div className={[styles.space80].join(' ')}/>
  </>


}
CarReviewAll.displayName = 'CarReviewAll'
export default CarReviewAll
